import {
  useApolloClient,
  useQuery,
  useMutation,
  useLazyQuery,
  QueryHookOptions,
  LazyQueryHookOptions
} from '@apollo/client';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  reducerResetCalculatePlan,
  reducerUpdateCalculatePlan,
  reducerUpdateCurrentPlan,
  reducerUpdateCurrentRate,
  reducerUpdateDetailInvoice,
  reducerUpdateInvoices,
  reducerUpdatePackagesStreamQuota,
  reducerUpdateStreamSimulation
} from 'src/features/billing';
import { useAlert } from 'src/hooks/useAlert';
import {
  ICalculatePlan,
  IPackageStream,
  IPackageStreamSimulation,
  IUserInvoice,
  IUserPlan,
  IUserPlanMinimal
} from 'src/models/billingSystem';
import { ITableWithPagination } from 'src/models/general';
import {
  BILLING_PLAN_CHECKOUT,
  BILLING_STREAM_CHECKOUT,
  SETTINGS
} from 'src/route';
import {
  BILLING_APPLY_PLAN,
  BILLING_APPSUMO_INVOICE_CHECKOUT_PLAN_STREAM,
  BILLING_INVOICE_CHECKOUT,
  BILLING_INVOICE_CHECKOUT_PLAN_STREAM,
  CHECKOUT_UPGRADE_PLAN,
  CREATE_PAYMENT_LINK_MANUALLY,
  GENERATE_USER_REFERENCE_CODE,
  UPGRADE_PLAN
} from './graphql/billing/mutation';
import {
  BILLING_GET_PAKAGE_PLAN_STREAM,
  BILLING_APPSUMO_GET_PLAN_STREAM_SIMULATION,
  CALCULATE_PLAN,
  GET_USER_INVOICE,
  GET_USER_INVOICE_BY_ID,
  GET_USER_PLAN,
  BILLING_APPSUMO_GET_USER_INVOICE,
  BILLING_APPSUMO_GET_USER_INVOICE_BY_ID,
  BILLING_CURRENCY_CONVERTER,
  GET_USER_SUBSCRIPTION,
  GET_INVOICE_DETAILS,
  GET_USER_INVOICE_V2,
  GET_USER_REFERENCE_CODE,
  GET_USER_REFERENCE_USAGE,
  GET_USER_REFERENCE_TOTAL_COMISSION
} from './graphql/billing/query';
import { PeriodSubscription, PlanItem } from 'src/models/register';

interface IPayloadPlanStream {
  packagePlanId: string;
  promoCode?: string;
}

interface IGetUserPlanResponse {
  billing_getUserPlan: IUserPlan;
}

interface IGetUserInvoicesResponse {
  billing_getUserInvoice: Array<IUserInvoice>;
}

interface IGetAppSumoInvoicesResponse {
  billingAppSumo_getUserInvoice: Array<IUserInvoice>;
}

interface IGetUserInvoiceByIdResponse {
  billing_getUserInvoiceById: IUserInvoice;
}

interface IGetAppSumoInvoiceByIdResponse {
  billingAppSumo_getUserInvoiceById: IUserInvoice;
}

interface IGetCalculatePlanResponse {
  billing_calculatePlan: ICalculatePlan;
}

interface IGetPackagePlanStreamResponse {
  billingAppSumo_getPackagePlanStream: Array<IPackageStream>;
}

interface IGetStreamSimulationResponse {
  billingAppSumo_getPlanStreamSimulation: IPackageStreamSimulation;
}

interface IGetCurrentRateResponse {
  user_currencyConverter: { value: number };
}

interface UserSubscription {
  id: string;
  userId: string;
  // period: number;
  currency: string;
  total: number;
  status: string;
}

interface BillableMetricPackage {
  name: string;
  description: string;
  unitLabel: string;
  quota: number;
}

interface UserInvoice {
  id: string;
  url: string | null;
  paymentStatus: string;
  periodStart: string;
  periodEnd: string;
  amount: number;
}

interface UserSubscriptionCycle {
  id: string;
  cycleNumber: number;
  isFreeTrial: boolean;
  status: string;
  total: number;
  periodStart: string;
  periodEnd: string;
  period: number;
}

export interface UserSubscriptionPackagePlan {
  id: string;
  name: string;
  displayName: string;
}

interface IGetUserSubscriptionResponse {
  billing_getUserSubscription: {
    userSubscription: UserSubscription;
    billableMetricPackage: BillableMetricPackage[];
    currentUserCycle: {
      userInvoice: UserInvoice;
      userSubscriptionCycle: UserSubscriptionCycle;
      packagePlan: UserSubscriptionPackagePlan;
    };
    nextUserCycle: {
      userInvoice: UserInvoice;
      userSubscriptionCycle: UserSubscriptionCycle;
      packagePlan: UserSubscriptionPackagePlan;
    };
    referalCode: string;
    isFirstPayment: boolean;
  };
}

interface IGetUserSubscriptionPayload {
  projectId: string;
}

interface IGetPaymentLinkManuallyPayload {
  userSubscriptionCycleId: string;
}

interface IGetPaymentLinkManuallyResponse {
  billing_createPaymentLinkManually: { url: string };
}

interface ICreateUpgradePlanPaymentLinkResponse {
  billing_createPaymentLinkBilling: { url: string };
}

interface ICreateUpgradePlanPaymentLinkPayload
  extends IGetPaymentLinkManuallyPayload {
  periodSubscription: PeriodSubscription;
  planItems?: PlanItem[];
  promoCodes: string[];
  packagePlanId?: string;
  referalCode?: string;
}

export interface IInvoice {
  invoiceNumber: string;
  invoiceDate: string;
  invoicePackages: Array<{
    billableMetric: {
      name: string;
      price: number;
      unitLabel: string;
      currency: string;
    };
    quota: number;
    total: number;
  }>;
  subTotal: number;
  discount: number;
  total: number;
  paidAt: string;
  periodEnd: string;
  periodStart: string;
  recipient: {
    fullName: string;
    projectName: string;
    companyName: string;
    email: string;
    subscriptionPeriod: number;
  };
  status: string;
}

interface IGetInvoiceDetailsResponse {
  billing_getUserInvoiceDetails: IInvoice;
}

interface IGetInvoiceDetailPayload {
  userInvoiceId: string;
}

interface IGetInvoiceV2Response {
  billing_getUserInvoiceV2: ITableWithPagination<UserInvoice>;
}

interface IGetInvoiceV2Payload {
  projectId: string;
  limit: number;
  page: number;
}

interface IUserReferenceCode {
  id: string;
  referenceCode: string;
  percentage: number;
  createdAt: string;
}

interface IUserReferenceUsage {
  email: string;
  totalRedeemed: number;
  createdAt: string;
}

interface IGetUserReferenceCodeResponse {
  billing_getUserReferenceCode: IUserReferenceCode;
}

interface IGenerateUserReferenceCodeResponse {
  billing_generateUserReferenceCode: IUserReferenceCode;
}

interface IGetUserReferenceUsagePayload {
  limit: number;
  page: number;
}

interface IGetUserReferenceUsageResponse {
  billing_getUserReferenceUsage: {
    totalRows: number;
    totalPages: number;
    page: number;
    limit: number;
    result: Array<IUserReferenceUsage>;
  };
}

interface IGetUserReferenceTotalComissinResponse {
  billing_getUserReferenceTotalCommission: number;
}

const useBillingSystem = function () {
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { invoices, currentPlan, packageStreamSimulation } = useAppSelector(
    (state) => state.storeBilling
  );
  const { handleClickAlert } = useAlert();

  const fetchUserPlan = async function (projectId: string) {
    try {
      return await client
        .query<IGetUserPlanResponse>({
          query: GET_USER_PLAN,
          variables: { projectId }
        })
        .then((response) => {
          return { ...response, data: response.data.billing_getUserPlan };
        });
    } catch (e) {
      console.log(e?.message);
    }
  };

  const getCurrentPlan = async function (projectId: string) {
    const response = await fetchUserPlan(projectId);
    if (!response?.data) return;
    dispatch(reducerUpdateCurrentPlan(response));
  };

  const fetchUserInvoices = async function (projectId: string) {
    try {
      return await client
        .query<IGetUserInvoicesResponse>({
          query: GET_USER_INVOICE,
          variables: { projectId }
        })
        .then((response) => {
          return { ...response, data: response.data.billing_getUserInvoice };
        });
    } catch (e) {
      console.log(e);
    }
  };

  const getUserInvoices = async function (projectId: string) {
    const response = await fetchUserInvoices(projectId);
    dispatch(reducerUpdateInvoices(response));
  };

  const fetchUserInvoicesAppSumo = async function (projectId: string) {
    try {
      return await client
        .query<IGetAppSumoInvoicesResponse>({
          query: BILLING_APPSUMO_GET_USER_INVOICE,
          variables: { projectId }
        })
        .then((response) => {
          return {
            ...response,
            data: response.data.billingAppSumo_getUserInvoice
          };
        });
    } catch (e) {
      console.log(e);
    }
  };

  const getUserInvoicesAppSumo = async function (projectId: string) {
    const response = await fetchUserInvoicesAppSumo(projectId);
    dispatch(reducerUpdateInvoices(response));
  };

  const fetchUserInvoiceById = async function (invoiceId: string) {
    try {
      return await client
        .query<IGetUserInvoiceByIdResponse>({
          query: GET_USER_INVOICE_BY_ID,
          variables: { invoiceId }
        })
        .then((response) => {
          return {
            ...response,
            data: response.data.billing_getUserInvoiceById
          };
        });
    } catch (e) {
      console.log(e);
    }
  };

  const getUserInvoiceById = async function (invoiceId: string) {
    const response = await fetchUserInvoiceById(invoiceId);
    dispatch(reducerUpdateDetailInvoice(response));
  };

  const fetchUserInvoiceAppSumoById = async function (invoiceId: string) {
    try {
      return await client
        .query<IGetAppSumoInvoiceByIdResponse>({
          query: BILLING_APPSUMO_GET_USER_INVOICE_BY_ID,
          variables: { invoiceId }
        })
        .then((response) => {
          return {
            ...response,
            data: response.data.billingAppSumo_getUserInvoiceById
          };
        });
    } catch (e) {
      console.log(e);
    }
  };

  const getUserInvoiceAppSumoById = async function (invoiceId: string) {
    const response = await fetchUserInvoiceAppSumoById(invoiceId);
    dispatch(reducerUpdateDetailInvoice(response));
  };

  const fetchUserCalculatePlan = async function (payload: IUserPlanMinimal) {
    try {
      return await client
        .query<IGetCalculatePlanResponse>({
          query: CALCULATE_PLAN,
          variables: payload
        })
        .then((response) => {
          return { ...response, data: response.data.billing_calculatePlan };
        });
    } catch (e) {
      console.log(e);
    }
  };

  const getUserCalculatePlan = async function (payload: IUserPlanMinimal) {
    const response = await fetchUserCalculatePlan(payload);
    dispatch(reducerUpdateCalculatePlan(response));
  };

  const fetchPackagePlanStream = async function () {
    try {
      return await client
        .query<IGetPackagePlanStreamResponse>({
          query: BILLING_GET_PAKAGE_PLAN_STREAM
        })
        .then((response) => {
          return {
            ...response,
            data: response.data.billingAppSumo_getPackagePlanStream
          };
        });
    } catch (e) {
      console.log(e);
    }
  };

  const getPackagePlanStream = async function () {
    const response = await fetchPackagePlanStream();
    dispatch(reducerUpdatePackagesStreamQuota(response));
  };

  const fetchPlanStreamSimulation = async function (
    payload: IPayloadPlanStream
  ) {
    try {
      return await client
        .query<IGetStreamSimulationResponse>({
          query: BILLING_APPSUMO_GET_PLAN_STREAM_SIMULATION,
          variables: payload
        })
        .then((response) => {
          return {
            ...response,
            data: response.data.billingAppSumo_getPlanStreamSimulation
          };
        });
    } catch (e) {
      console.log(e);
    }
  };

  const getPlanStreamSimulation = async function (payload: IPayloadPlanStream) {
    dispatch(
      reducerUpdateStreamSimulation({
        ...packageStreamSimulation,
        loading: true
      })
    );
    const response = await fetchPlanStreamSimulation(payload);
    dispatch(reducerUpdateStreamSimulation(response));
    navigate(BILLING_STREAM_CHECKOUT);
  };

  const fetchCurrentRate = async function (payload: string) {
    try {
      return await client
        .query<IGetCurrentRateResponse>({
          query: BILLING_CURRENCY_CONVERTER,
          variables: { countryId: payload }
        })
        .then((response) => {
          return {
            ...response,
            data: response.data.user_currencyConverter.value
          };
        });
    } catch (e) {
      console.log(e);
    }
  };

  const getCurrentRate = async function (payload: string) {
    const response = await fetchCurrentRate(payload);
    dispatch(reducerUpdateCurrentRate(response));
  };

  const [postCheckoutPlanStream, responseCheckoutPlanStream] = useMutation(
    BILLING_INVOICE_CHECKOUT_PLAN_STREAM,
    {
      onCompleted: ({ billingAppSumo_invoiceCheckoutPlanStream }) => {
        reducerUpdateInvoices({
          ...invoices,
          data: [...invoices.data, billingAppSumo_invoiceCheckoutPlanStream]
        });
        reducerResetCalculatePlan();
      }
    }
  );

  const [postUpgradePlan, responseUpgradePlan] = useMutation(UPGRADE_PLAN, {
    onCompleted: ({ billing_upgradePlan }) => {
      reducerUpdateInvoices({
        ...invoices,
        data: [...invoices.data, billing_upgradePlan]
      });
      navigate(`${BILLING_PLAN_CHECKOUT}?invoice_id=${billing_upgradePlan.id}`);
      reducerResetCalculatePlan();
    }
  });

  const [postRemoveObjects, responsePostRemoveObjects] = useMutation(
    UPGRADE_PLAN,
    {
      onCompleted: ({ billing_upgradePlan }) => {
        console.log(billing_upgradePlan.planCalculation.total);
        if (billing_upgradePlan.planCalculation.total !== 0) {
          reducerUpdateInvoices({
            ...invoices,
            data: [invoices.data, billing_upgradePlan]
          });
          navigate(
            `${BILLING_PLAN_CHECKOUT}?invoice_id=${billing_upgradePlan.id}`
          );
        } else {
          navigate(`${SETTINGS}?tab=billing`);
        }
      },
      onError(error) {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: "Couldn't remove object, please try again",
          severity: 'error'
        });
        console.log(error);
      }
    }
  );

  const [postApplyPlan, responseApplyPlan] = useMutation(BILLING_APPLY_PLAN, {
    onCompleted: ({ billing_applyPlan }) => {
      reducerUpdateCurrentPlan({
        ...currentPlan,
        data: { ...currentPlan.data, isApply: false }
      });
      reducerUpdateInvoices({
        ...invoices,
        data: [...invoices.data, billing_applyPlan]
      });
    }
  });

  const [postInvoiceCheckout, responseInvoiceCheckout] = useMutation(
    BILLING_INVOICE_CHECKOUT,
    {
      onCompleted: ({ billing_invoiceCheckout }) => {
        navigate(billing_invoiceCheckout.url);
      }
    }
  );

  const [postAppSumoCheckoutPlanStream, resPostAppSumoCheckoutPlanStream] =
    useMutation(BILLING_APPSUMO_INVOICE_CHECKOUT_PLAN_STREAM, {
      onCompleted: (response) => {
        const data = response?.billingAppSumo_invoiceCheckoutPlanStream;
        if (!data) return;
        window.location.href = data.url;
      }
    });

  const useGetUserSubscription = (
    options?: QueryHookOptions<
      IGetUserSubscriptionResponse,
      IGetUserSubscriptionPayload
    >
  ) => {
    return useQuery<IGetUserSubscriptionResponse, IGetUserSubscriptionPayload>(
      GET_USER_SUBSCRIPTION,
      {
        onError: (error) => {
          console.log(error);
        },
        fetchPolicy: 'cache-first',
        ...options
      }
    );
  };

  const [postCreatePaymentLinkManually, responseCreatePaymentLinkManually] =
    useMutation<
      IGetPaymentLinkManuallyResponse,
      IGetPaymentLinkManuallyPayload
    >(CREATE_PAYMENT_LINK_MANUALLY);

  const useGetInvoiceDetails = (
    options?: LazyQueryHookOptions<
      IGetInvoiceDetailsResponse,
      IGetInvoiceDetailPayload
    >
  ) => {
    return useLazyQuery<IGetInvoiceDetailsResponse, IGetInvoiceDetailPayload>(
      GET_INVOICE_DETAILS,
      {
        onError: (error) => {
          console.log(error);
        },
        ...options
      }
    );
  };

  const [createUpgradePlanPaymentLink, responseUpgradePlanPaymentLink] =
    useMutation<
      ICreateUpgradePlanPaymentLinkResponse,
      ICreateUpgradePlanPaymentLinkPayload
    >(CHECKOUT_UPGRADE_PLAN);

  const useGetInvoiceListV2 = (
    option?: QueryHookOptions<IGetInvoiceV2Response, IGetInvoiceV2Payload>
  ) => {
    return useQuery<IGetInvoiceV2Response, IGetInvoiceV2Payload>(
      GET_USER_INVOICE_V2,
      {
        onError: (error) => {
          console.log(error);
        },
        ...option
      }
    );
  };

  const useGetUserReferenceCode = (
    option?: QueryHookOptions<IGetUserReferenceCodeResponse>
  ) => {
    return useQuery<IGetUserReferenceCodeResponse>(GET_USER_REFERENCE_CODE, {
      onError: (error) => {
        console.log(error);
      },
      ...option
    });
  };

  const [generateUserReferenceCode, responseGenerateUserReferenceCode] =
    useMutation<IGenerateUserReferenceCodeResponse>(
      GENERATE_USER_REFERENCE_CODE
    );

  const useGetReferenceCodeUsage = (
    option?: QueryHookOptions<
      IGetUserReferenceUsageResponse,
      IGetUserReferenceUsagePayload
    >
  ) => {
    return useQuery<
      IGetUserReferenceUsageResponse,
      IGetUserReferenceUsagePayload
    >(GET_USER_REFERENCE_USAGE, {
      onError: (error) => {
        console.log(error);
      },
      ...option
    });
  };

  const useGetUserReferenceTotalComission = (
    option?: QueryHookOptions<IGetUserReferenceTotalComissinResponse>
  ) => {
    return useQuery<IGetUserReferenceTotalComissinResponse>(
      GET_USER_REFERENCE_TOTAL_COMISSION,
      {
        onError: (error) => {
          console.log(error);
        },
        ...option
      }
    );
  };

  return {
    getCurrentPlan,
    getUserInvoices,
    getUserInvoiceById,
    getUserCalculatePlan,
    postUpgradePlan,
    responseUpgradePlan,
    postApplyPlan,
    responseApplyPlan,
    postInvoiceCheckout,
    responseInvoiceCheckout,
    getPackagePlanStream,
    getPlanStreamSimulation,
    postCheckoutPlanStream,
    responseCheckoutPlanStream,
    getUserInvoicesAppSumo,
    getUserInvoiceAppSumoById,
    postAppSumoCheckoutPlanStream,
    resPostAppSumoCheckoutPlanStream,
    getCurrentRate,
    postRemoveObjects,
    responsePostRemoveObjects,
    useGetUserSubscription,
    postCreatePaymentLinkManually,
    responseCreatePaymentLinkManually,
    useGetInvoiceDetails,
    useGetInvoiceListV2,
    createUpgradePlanPaymentLink,
    responseUpgradePlanPaymentLink,
    useGetUserReferenceCode,
    generateUserReferenceCode,
    responseGenerateUserReferenceCode,
    useGetReferenceCodeUsage,
    useGetUserReferenceTotalComission
  };
};

export default useBillingSystem;
